import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const iso = (d) => d.toISOString().slice(0, 10);
const mondayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };

export default function TimesheetPage() {
  const { projectId } = useParams();
  const { user } = useAuth();
  const [monday, setMonday] = useState(() => mondayOf(new Date()));
  const [entries, setEntries] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [extraRows, setExtraRows] = useState([]);
  const [pickTask, setPickTask] = useState("");

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/artifacts/time_entries`).then((r) => setEntries(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/projects/${projectId}/schedule`).then((r) => setTasks(r.data.tasks || [])).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => [...Array(7)].map((_, i) => { const d = new Date(monday); d.setDate(d.getDate() + i); return iso(d); }), [monday]);
  const mine = useMemo(() => entries.filter((e) => e.userId === user?.id || e.createdBy === user?.id), [entries, user]);
  const weekMine = useMemo(() => mine.filter((e) => days.includes(e.date)), [mine, days]);

  const rowTaskIds = useMemo(() => {
    const ids = new Set(weekMine.map((e) => e.taskId));
    extraRows.forEach((t) => ids.add(t));
    return [...ids];
  }, [weekMine, extraRows]);

  const entryFor = (taskId, date) => weekMine.find((e) => e.taskId === taskId && e.date === date);
  const taskName = (id) => tasks.find((t) => t.id === id)?.name || weekMine.find((e) => e.taskId === id)?.taskName || "—";

  const saveCell = async (taskId, date, value) => {
    const hours = parseFloat(value) || 0;
    const existing = entryFor(taskId, date);
    try {
      if (existing && hours === 0) await api.delete(`/projects/${projectId}/artifacts/time_entries/${existing.id}`);
      else if (existing) {
        if (existing.hours === hours) return;
        await api.put(`/projects/${projectId}/artifacts/time_entries/${existing.id}`, { ...existing, hours });
      } else if (hours > 0) {
        await api.post(`/projects/${projectId}/artifacts/time_entries`, { taskId, taskName: taskName(taskId), date, hours });
      } else return;
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const weekTotal = weekMine.reduce((s, e) => s + (e.hours || 0), 0);
  const allTimeByTask = useMemo(() => {
    const m = {};
    entries.forEach((e) => { m[e.taskId] = (m[e.taskId] || 0) + (e.hours || 0); });
    return m;
  }, [entries]);

  return (
    <div data-testid="timesheet-page">
      <PageHeader overline="Plan · Actual effort capture" title="My Timesheet">
        <div className="text-xs text-muted-foreground mt-1">Logged hours feed the Capacity view (actuals) and give Finance real effort per task.</div>
      </PageHeader>

      <div className="flex items-center gap-3 mt-3 mb-3">
        <div className="inline-flex items-center border border-border">
          <button data-testid="timesheet-prev-week" onClick={() => setMonday((m) => { const d = new Date(m); d.setDate(d.getDate() - 7); return d; })} className="p-2 hover:bg-muted"><ChevronLeft className="h-4 w-4" /></button>
          <span data-testid="timesheet-week-label" className="px-3 text-sm font-semibold">Week of {days[0]}</span>
          <button data-testid="timesheet-next-week" onClick={() => setMonday((m) => { const d = new Date(m); d.setDate(d.getDate() + 7); return d; })} className="p-2 hover:bg-muted"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <Select value={pickTask} onValueChange={setPickTask}>
          <SelectTrigger data-testid="timesheet-task-select" className="w-72 h-9"><SelectValue placeholder="Add a task row…" /></SelectTrigger>
          <SelectContent>
            {tasks.filter((t) => !t.isMilestone && !rowTaskIds.includes(t.id)).map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.wbsCode ? `${t.wbsCode} ` : ""}{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button data-testid="timesheet-add-row-btn" className="rounded-none h-9" disabled={!pickTask}
          onClick={() => { setExtraRows((r) => [...r, pickTask]); setPickTask(""); }}><Plus className="h-4 w-4" /></Button>
        <span className="ml-auto text-sm">Week total: <span data-testid="timesheet-week-total" className="font-display font-black text-lg">{weekTotal}h</span></span>
      </div>

      <div className="border border-border bg-card overflow-auto">
        <table className="w-full data-table text-sm">
          <thead><tr><th>Task</th>{DAYS.map((d, i) => <th key={d} className="text-center w-20">{d}<div className="text-[9px] font-normal text-muted-foreground">{days[i].slice(5)}</div></th>)}<th className="text-center">Week</th><th className="text-center">All time</th></tr></thead>
          <tbody>
            {rowTaskIds.length === 0 && <tr><td colSpan={10} className="text-center text-muted-foreground py-8">No hours this week — pick a task above to start logging.</td></tr>}
            {rowTaskIds.map((tid) => {
              const rowTotal = weekMine.filter((e) => e.taskId === tid).reduce((s, e) => s + (e.hours || 0), 0);
              return (
                <tr key={tid} data-testid="timesheet-row">
                  <td className="max-w-xs font-medium">{taskName(tid)}</td>
                  {days.map((d) => (
                    <td key={d} className="p-1 text-center">
                      <input data-testid={`ts-cell-${tid}-${d}`} type="number" min="0" max="24" step="0.5"
                        defaultValue={entryFor(tid, d)?.hours ?? ""}
                        onBlur={(e) => saveCell(tid, d, e.target.value)}
                        className="w-14 border border-input px-1 py-1 text-xs text-center bg-background" />
                    </td>
                  ))}
                  <td className="text-center font-semibold">{rowTotal}h</td>
                  <td className="text-center text-muted-foreground">{allTimeByTask[tid] || 0}h</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
